import { css } from "@emotion/react";
import React, { useContext, useMemo } from "react";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { type UnknownProps } from "../@types/UnknownProps";
import { Typography } from "../Typography";
import { RelativeTimeFormat } from "../@utils";
import { LocaleContext } from "../LocaleProvider";
import type { Unit } from "relative-time-format";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);

export interface TimelineLabelProps {
  formattedDate: string;
}

const DefaultLabel = ({ formattedDate }: TimelineLabelProps) => {
  return (
    <Typography
      css={css`
        display: flex;
        gap: 16px;
        align-items: center;
        &::before,
        &::after {
          flex-grow: 1;
          height: 1px;
          content: "";
          background-color: currentColor;
        }
      `}
    >
      {formattedDate}
    </Typography>
  );
};

export type TimelineItemProps<Props extends UnknownProps> = Props & {
  date: string;
  index: number;
};

export interface TimelineProps<
  Props extends UnknownProps,
> extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * TimelineProps.datesで指定されたデータをpropsとして受け取り表示するためのコンポーネントを指定します。
   */
  renderItem?: React.ComponentType<TimelineItemProps<Props>>;
  /**
   * 日付と、日付に対応するデータ群を指定します。
   *
   * 昇順でkeyがソートされていることを前提としている点に注意してください。
   *
   * ソート処理が面倒な場合はUI側での処理を検討します。
   *
   * ここで値として指定された配列の型はTimelineProps.itemの引数に用いられます。
   *
   * dateのフォーマットはYYYYMMDDまたはYYYY-MM-DDです。number(ms)には後に対応するかもしれません。
   *
   * Date型を利用したいという強い要望があった場合はMapに切り替えることを検討します。
   *
   */
  dates: Record<string, Props[]>;
  /**
   * 区分ごとのラベルの表記を指定します。
   *
   * @default {year: numeric ,month: numeric, day: numeric}
   */
  format?: "relative" | Intl.DateTimeFormatOptions;
  /**
   * 区分を指定する。原点を中心として両側に、順に指定された区分点を配置していく。
   *
   * 原点のデフォルトは"今日"。
   *
   * 過去から未来へ昇順でソートされていることを前提にしています。
   * ソート処理が難しい場合はUI側での処理を検討します。
   *
   * @default []
   */
  separators?: {
    unit: "day" | "month" | "year" | "week";
    value: number;
  }[];
  /**
   * 基準日を指定する。
   * @default today
   */
  date?: dayjs.ConfigType;
  /**
   * フォーマットされた日付を受け取り、ラベルとして表示するコンポーネントを指定します。
   */
  renderLabel?: (props: TimelineLabelProps) => React.JSX.Element;
}

const getDatesEntries = <E extends UnknownProps>(
  dates: Record<string, E[]>,
  separators: {
    unit: "day" | "month" | "year" | "week";
    value: number;
  }[],
  origin: dayjs.Dayjs,
) => {
  if (separators.length === 0) {
    return {
      prevPropsArray: [],
      datesEntries: Object.entries(dates).map(([date, propsArray]) => {
        const diff = dayjs(date).diff(origin, "day");
        return [
          date,
          propsArray.map((props) => ({
            ...props,
            date,
          })),
          {
            unit: "day",
            diff,
          },
        ] as const;
      }),
    };
  }

  const copy = { ...dates };
  // prevProps
  let prevProps: (E & { date: string })[] = [];
  const min = origin.add(separators[0].value, separators[0].unit);
  for (const date in copy) {
    if (dayjs(date).isBefore(min, "day")) {
      const add = copy[date].map((props) => ({ ...props, date }));
      prevProps = prevProps.concat(add);
      delete copy[date];
    } else {
      break;
    }
  }
  let results: (readonly [
    string,
    (E & {
      date: string;
    })[],
    {
      unit: string;
      diff: number;
    },
  ])[] = [];
  separators.map(({ unit, value }, index, original) => {
    const min = origin.add(value, unit);
    const max =
      separators.length - 1 === index
        ? dayjs("9999/12/24")
        : origin.add(original[index + 1].value, original[index + 1].unit);
    let propsArray: (E & { date: string })[] = [];
    for (const date in copy) {
      if (dayjs(date).isBetween(min, max, "day", "[)")) {
        const add = copy[date].map((props) => ({ ...props, date }));
        propsArray = propsArray.concat(add);
        delete copy[date];
      } else {
        break;
      }
    }
    if (propsArray.length > 0) {
      results = results.concat([
        [
          min.format("YYYYMMDD"),
          propsArray,
          {
            unit,
            diff: value,
          },
        ],
      ]);
    }
  });
  return {
    prevPropsArray: prevProps,
    datesEntries: results,
  };
};

const makeRenderItems =
  <E extends UnknownProps>(
    Item: React.ComponentType<E & { date: string; index: number }>,
  ) =>
  (propsArray: (E & { date: string })[]) => {
    return propsArray.map((props, index) => {
      const ItemComponent = Item as React.ComponentType<any>;
      return (
        <ItemComponent
          {...props}
          index={index}
          key={`${props.date}.${index}`}
        />
      );
    });
  };

export const Timeline = <E extends UnknownProps>({
  renderItem,
  dates,
  format = { year: "numeric", month: "numeric", day: "numeric" },
  separators = [],
  date = dayjs().startOf("day"),
  renderLabel: Label = DefaultLabel,
  ...props
}: TimelineProps<E>) => {
  const locale = useContext(LocaleContext);
  const rtf = useMemo(() => {
    return RelativeTimeFormat(locale, {
      numeric: "auto",
    });
  }, [locale]);
  const { prevPropsArray, datesEntries } = getDatesEntries<E>(
    dates,
    separators,
    dayjs(date),
  );

  const renderItems = useMemo(() => {
    const Item = renderItem;
    if (Item === undefined) {
      throw new Error("item or renderItem is required");
    }
    return makeRenderItems<E>(Item);
  }, [renderItem]);

  return datesEntries.length > 0 ? (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 16px;
      `}
      {...props}
    >
      {prevPropsArray.length > 0 && <div>{renderItems(prevPropsArray)}</div>}
      {datesEntries.map(([value, propsArray, { unit, diff }]) => {
        const formatedDate =
          format === "relative"
            ? rtf.format(diff, unit as Unit)
            : dayjs(value).toDate().toLocaleDateString(locale, format);
        return (
          <React.Fragment key={formatedDate}>
            <Label formattedDate={formatedDate} />
            <div>{renderItems(propsArray)}</div>
          </React.Fragment>
        );
      })}
    </div>
  ) : null;
};
